import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { INSPECTOR_ROLE_CODE } from '@pmc/shared';
import { AdminLayout } from './components/AdminLayout';
import { LoadingPage } from './components/common';
import { useAuth } from './lib/auth';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const TransactionsPage = lazy(() => import('./pages/TransactionsPage'));
const ApplicationsPage = lazy(() => import('./pages/ApplicationsPage'));
const ApplicationDetailPage = lazy(() => import('./pages/ApplicationDetailPage'));
const QueriesPage = lazy(() => import('./pages/QueriesPage'));
const FaqsPage = lazy(() => import('./pages/FaqsPage'));
const ServiceBuilderPage = lazy(() => import('./pages/ServiceBuilderPage'));
const ServiceEditPage = lazy(() => import('./pages/ServiceEditPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));

/** Everything except /login sits behind an authenticated shell. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <LoadingPage />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}
// testing qa
/**
 * A field inspector's own login (see AdminLayout's INSPECTOR_NAV) never
 * links to these routes, but the server also rejects them for that role —
 * this guard just avoids a broken/empty page if they're reached by URL
 * directly, redirecting to the one page an inspector actually has.
 */
function RequireAdminRole({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user?.roleCode === INSPECTOR_ROLE_CODE) return <Navigate to="/applications" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Suspense fallback={<LoadingPage />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          element={
            <RequireAuth>
              <AdminLayout />
            </RequireAuth>
          }
        >
          <Route
            path="/"
            element={
              <RequireAdminRole>
                <DashboardPage />
              </RequireAdminRole>
            }
          />
          <Route
            path="/transactions"
            element={
              <RequireAdminRole>
                <TransactionsPage />
              </RequireAdminRole>
            }
          />
          <Route path="/applications" element={<ApplicationsPage />} />
          <Route path="/applications/:id" element={<ApplicationDetailPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route
            path="/queries"
            element={
              <RequireAdminRole>
                <QueriesPage />
              </RequireAdminRole>
            }
          />
          <Route
            path="/faqs"
            element={
              <RequireAdminRole>
                <FaqsPage />
              </RequireAdminRole>
            }
          />
          <Route
            path="/services"
            element={
              <RequireAdminRole>
                <ServiceBuilderPage />
              </RequireAdminRole>
            }
          />
          <Route
            path="/services/new"
            element={
              <RequireAdminRole>
                <ServiceEditPage />
              </RequireAdminRole>
            }
          />
          <Route
            path="/services/:code/edit"
            element={
              <RequireAdminRole>
                <ServiceEditPage />
              </RequireAdminRole>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
